import React from "react";
import { TPaginationParams } from "Pages/GMS/Performance/analytics";
import { PropsWithChildren, createContext, useContext, useState } from "react";
import {
    promoteProductData,
    checkJobStatus, getIndustryCategoriesData, getMerchantConfigData, updateMerchantConfigData
} from "../Pages/DefineModel/ModelServices/ModalServices";
import { toast } from "react-toastify";
import { AppContext } from "Context/AppContext";
import {TField, TFieldClass, TUpsertISRProjectDataModelsInput} from "../Pages/DefineModel/model";
import ServiceWrapper from "../../../../Services/ServiceWrapper";
import AuthManager from "../../../../Common/AuthManager";
import AppSettings from "../../../../Settings/AppSettings";

export const ISRContext = createContext<any>(undefined);

export const ISRContextProvider = ({children}: PropsWithChildren) => {
    const appContext = useContext(AppContext);
    const industryId = appContext.get_selected_industry() && appContext?.get_selected_industry()?.id;
    const initialUserProjectDataModels: TUpsertISRProjectDataModelsInput = { project: { ISRName:  "", dataModels: [], fields: [], productCategories: [], connectors: [], id: '' }, rules: [], };
    const [paginationParams, setPaginationParams] = useState<TPaginationParams>({pageNumber: 1, pageSize: 10});
    const [disableButton, setDisableButton] = useState<boolean>(false);
    const [updateLocalState, setUpdateLocalState] = useState<boolean>(false);
    const [doesUIDefinitionAPICalled, setDoesUIDefinitionAPICalled] = useState<boolean>(false);
    const [refreshCounter, setRefreshCounter] = useState<number>(0);
    const [fieldClasses, setFieldClasses] = useState<TFieldClass[]>([]);
    const [modelClasses, setModelClasses] = useState<TFieldClass[]>([]);
    const [networkUserProjectDataModels, setNetworkUserProjectDataModels] = useState<TUpsertISRProjectDataModelsInput>(JSON.parse(JSON.stringify(initialUserProjectDataModels)));
    const [fieldUpdate, setFieldUpdate] = useState<TField[]>([]);
    const [fieldDelete, setFieldDelete] = useState<string[]>([]);
    

    const promoteProducts = async (industryId: string, categories: []) => {
        try {
            toast.info('Data promotion has started ...');
            const responseData = await promoteProductData(industryId, categories);
            if (responseData?.done) {
                const id = toast.loading(responseData?.message)
                await checkJobStatus(responseData.jobid, id);
            } else {
                toast.error(responseData?.message);
            }
        } catch (error) {
            console.log('Error', error);
        }
    }

    const getIndustryCategories = async (industryId: string) => {
        try {
            const responseData = await getIndustryCategoriesData(industryId);
            if (!responseData?.done) {
                toast.error(responseData?.message);
            }
            return responseData;
        } catch (error) {
            console.log('Error', error);
        }
    }

    const getMerchantConfig = async (channelKey: string) => {
        try {
            const responseData = await getMerchantConfigData(channelKey);
            if (!responseData?.done) {
                toast.error(responseData?.message);
            }
            return responseData;
        } catch (error) {
            console.log('Error', error);
        }
    }

    const updateMerchantConfig = async (channelKey: string, merchantConfigData: { config_attr: any; value: any }[]) => {
        try {
            const id = toast.loading('Saving merchant config...')
            const responseData = await updateMerchantConfigData(channelKey, merchantConfigData);
            if (responseData?.done) {
                toast.update(id, {render: responseData?.message, type: "success", isLoading: false, autoClose: 2000});
            } else {
                toast.update(id, {render: responseData?.message ?? '', type: "error", isLoading: false, autoClose: 2000});
            }
            return responseData;
        } catch (error) {
            console.log('Error', error);
        }
    }

    // Handle image upload
    const uploadImage = async (file: any, projectId: any) => {
      if(!file) return
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp'
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only image files (JPEG, PNG, GIF, WEBP) are allowed');
        return;
      }
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error('File size must be less than 10MB');
        return;
      }

      // Create FormData
      const formData = new FormData();

      try {
        let fileToUpload = file;

        // Convert WebP to PNG if necessary
        if (file.type === 'image/webp') {
          fileToUpload = await convertWebPToBlob(file);
        }

        formData.append('file', fileToUpload);

        // Custom upload function
        const response = await ServiceWrapper.doPost(
            `${AppSettings.GMS_BACKEND_API_URL}/isr/models/${projectId ?? ''}/image/upload`,
          formData,
          null,
          AuthManager.getGmsToken(),
          true
        );

        if (!response.data.done) {
          throw new Error(`Upload failed: ${response.message}`);
        }

        //response.data.data
        return response.data.data;
      } catch (error) {
        console.error('Upload error:', error);
      }
    };

    // WebP conversion utility
    const convertWebPToBlob = (file: File): Promise<File> => {
        return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          if (blob) {
            const convertedFile = new File([blob], file.name.replace('.webp', '.png'), {
              type: 'image/png'
            });
            resolve(convertedFile);
          } else {
            reject(new Error('Failed to convert WebP to PNG'));
          }
        }, 'image/png');
      };

      img.onerror = () => reject(new Error('Failed to load WebP image'));
      img.src = URL.createObjectURL(file);
    });
    };

    function isValidUrl(url: string) {
      try {
        new URL(url);
        return true;
      } catch (err) {
        return false;
      }
    }

    const clearClassData = () => {
        setFieldClasses([]);
        setModelClasses([]);
        setRefreshCounter(0);
        setDoesUIDefinitionAPICalled(false);
        setUpdateLocalState(false);
        setNetworkUserProjectDataModels(initialUserProjectDataModels);
    }
    
    return (
        <ISRContext.Provider value={
            {
                paginationParams,
                setPaginationParams, 
                promoteProducts, 
                setDisableButton, 
                disableButton, 
                industryId,
                refreshCounter,
                setRefreshCounter,
                fieldClasses,
                setFieldClasses,
                modelClasses,
                setModelClasses,
                clearClassData,
                doesUIDefinitionAPICalled,
                setDoesUIDefinitionAPICalled,
                initialUserProjectDataModels,
                networkUserProjectDataModels,
                setNetworkUserProjectDataModels,
                updateLocalState,
                setUpdateLocalState,
                fieldUpdate,
                setFieldUpdate,
                fieldDelete,
                setFieldDelete,
                getIndustryCategories,
                getMerchantConfig,
                updateMerchantConfig,
                uploadImage,
                isValidUrl
            }}>
            {children}
        </ISRContext.Provider>
    );
}